import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import tickets from '../../models/tickets';
import helpers from '../../helpers/common';
import useRootHook from '../../hooks/useRoot';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import PageHeader from '../layout/PageHeader';
import SectionCard from '../layout/SectionCard';
import { Chip, TextField, Typography, Box } from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';

const statusLabels = { open: 'Open', answered: 'Answered', closed: 'Closed' };
const statusColors = { open: 'warning', answered: 'success', closed: 'default' };

export default function TicketView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [ticket, setTicket] = useState(null);
    const [adminReply, setAdminReply] = useState('');
    const [adminStatus, setAdminStatus] = useState('');
    const [saving, setSaving] = useState(false);
    const { setShowLoader, userData } = useRootHook();
    const isAdmin = userData && [helpers.roles.A, helpers.roles.S].includes(userData.role);

    useEffect(() => {
        setShowLoader(true);
        tickets.get(id)
            .then((t) => {
                setTicket(t);
                if (t && isAdmin) {
                    setAdminReply(t.admin_reply || '');
                    setAdminStatus(t.status || '');
                }
                setShowLoader(false);
            })
            .catch(() => setShowLoader(false));
    }, [id, isAdmin]);

    const handleSaveReply = () => {
        if (!ticket || !isAdmin) return;
        setSaving(true);
        tickets.edit({ id: ticket.id, admin_reply: adminReply, status: adminStatus })
            .then(() => {
                setTicket((prev) => (prev ? { ...prev, admin_reply: adminReply, status: adminStatus } : null));
                setSaving(false);
            })
            .catch(() => setSaving(false));
    };

    if (!ticket) return null;

    return (
        <React.Fragment>
            <Grid size={12}>
                <PageHeader
                    title={`Ticket #${ticket.id}`}
                    breadcrumbs={[['Dashboard', '/'], ['Tickets', '/tickets'], [`#${ticket.id}`, `/tickets/${ticket.id}`]]}
                    actions={
                        <>
                            <Button startIcon={<ArrowBack />} onClick={() => navigate('/tickets')}>
                                Back
                            </Button>
                        </>
                    }
                />
            </Grid>
            <Grid size={12}>
                <SectionCard>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">Subject</Typography>
                        <Typography variant="body1">{ticket.subject}</Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                        <Chip size="small" label={statusLabels[ticket.status] || ticket.status} color={statusColors[ticket.status] || 'default'} />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">Message</Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{ticket.message}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        Created {helpers.getDateTime(ticket.created)} · Author: {ticket.user_role === 'M' ? 'Merchant' : ticket.user_role === 'R' ? 'Referral' : 'Admin'} #{ticket.user_id}
                    </Typography>

                    {ticket.admin_reply && (
                        <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary">Support reply</Typography>
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{ticket.admin_reply}</Typography>
                        </Box>
                    )}

                    {isAdmin && (
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Reply / Status</Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Reply"
                                value={adminReply}
                                onChange={(e) => setAdminReply(e.target.value)}
                                sx={{ mb: 1 }}
                            />
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                <select
                                    value={adminStatus}
                                    onChange={(e) => setAdminStatus(e.target.value)}
                                    style={{ padding: '8px 12px', borderRadius: 4, border: '1px solid #ccc' }}
                                >
                                    <option value="open">Open</option>
                                    <option value="answered">Answered</option>
                                    <option value="closed">Closed</option>
                                </select>
                                <Button variant="contained" onClick={handleSaveReply} disabled={saving}>
                                    {saving ? 'Saving…' : 'Save reply'}
                                </Button>
                            </Box>
                        </Box>
                    )}
                </SectionCard>
            </Grid>
        </React.Fragment>
    );
}
